// Shared init for web and portal bundles
// tslint:disable:no-console

import moment from "moment";
import React from "react";
import * as ReactDOM from "react-dom";
import { AppContainer } from "react-hot-loader";
import { Provider } from "react-redux";
import { applyRouterMiddleware, browserHistory, match, Router } from "react-router";
import { routerMiddleware, routerReducer, syncHistoryWithStore } from "react-router-redux";
import * as Redux from "redux";
import promiseMiddleware from "redux-promise-middleware";
import _ from "underscore";
import { handleError } from "../../errorHandler";
import { GoogleOptimizeProvider } from "@travellocal/ui";
import { scrollOnTransition } from "../../helpers/reactRouterHelper";
import { WindowDimensionsProvider } from "../../../core/atoms";
import { storeMiddleware } from "../../state/middleware";
import { SharedActions } from "../actions/sharedActions";
import { DialogContextProvider } from "../../../core/atoms";

const rootElementId = "host";

const errorIgnorePatterns = [
  /ResizeObserver loop limit exceeded/i,
];

export const getInitialReduxState = () => {
  return (window as any).__INITIAL_STATE__;
};

/**
 * Common code to render the client app with a redux store.
 */
export const renderApp = (
  reducers: IStoreReducers<IWebStoreReducerState> | IStoreReducers<IAppStoreReducerState>,
  routeFactory: (store: Redux.Store<any>, serverRender: boolean) => JSX.Element,
  initialState: any,
  onRenderComplete?: () => void,
) => {
  const reducer = Redux.combineReducers(_.extend({ routing: routerReducer }, reducers));
  const browserMiddlewares = [
    routerMiddleware(browserHistory),
    promiseMiddleware(),
    storeMiddleware,
  ];

  const compose = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || Redux.compose;

  const store = Redux.createStore(reducer, initialState, compose(Redux.applyMiddleware(...browserMiddlewares)));

  // Enhanced history that syncs navigation events with the store - for time travel debugging
  const syncedHistory = syncHistoryWithStore(browserHistory, store);
  syncedHistory.listenBefore((location) => {
    store.dispatch({ type: SharedActions.Errors.ClearError });
    store.dispatch({ type: SharedActions.Router.LocationChanged, payload: location });
  });

  window.onerror = (message, source, lineno, colno, error) => {
    if (!error) {
      error = new Error(message as string);
    }

    if (errorIgnorePatterns.find(errorRegExp => errorRegExp.test(error.message)) != null) {
      console.log(`We have trapped and ignored the following error, as we think it is harmless: '${error.message}'`);
      return;
    }

    handleError(error, store.dispatch);
  };

  moment.locale("en-GB");
  console.info(`Moment: ${moment.locale()}`);

  const routes = routeFactory(store, false);

  // #1968 Use match API so that server render works
  // https://github.com/ReactTraining/react-router/blob/v3/docs/guides/ServerRendering.md#async-routes
  match({ history: syncedHistory, routes }, (error, redirectLocation, renderProps) => {
    if (redirectLocation) {
      // #2844 TODO not sure this is ideal behaviour but reqd for hotfix
      syncedHistory.replace(redirectLocation);
      window.location.reload(true);
      return;
    }

    ReactDOM.hydrate((
      <AppContainer>
        <Provider store={store}>
          <GoogleOptimizeProvider>
            <WindowDimensionsProvider>
              <DialogContextProvider>
                <Router {...renderProps} render={applyRouterMiddleware(scrollOnTransition)} />
              </DialogContextProvider>
            </WindowDimensionsProvider>
          </GoogleOptimizeProvider>
        </Provider>
      </AppContainer>
    ), document.getElementById(rootElementId), onRenderComplete);

    console.info(`Rendered to ${rootElementId} ${new Date().toISOString()}`);
  });

  // TODO
  // Abandoning HMR for react components until it's more stable
  // if (module.hot) {
  //   module.hot.accept("./appRoot", () => {
  //     console.log("meow meow");

  //     let newRoutes = require("./appRoot").default;
  //     renderApp(() => routeFactory(store, false));
  //   });
  // }
};
